// components/SummaryStats.jsx
import React, { useState, useEffect, useContext, useCallback } from 'react';
import './SummaryStats.css'; // Optional: for styling
import { AuthContext } from '../../../contexts/AuthContext';

const SummaryStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // Get user and token from AuthContext
  const { user, token } = useContext(AuthContext);

  // Memoize the fetch function to prevent unnecessary re-renders
  const fetchSummaryStats = useCallback(async () => {
    if (!token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching fresh dashboard stats...'); // Debug log
      
      // Add a cache-busting timestamp to the URL
      const timestamp = new Date().getTime();
      const url = `https://aceswafund.org/welfare/welfare-system/get_dashboard_stats?_=${timestamp}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
           'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
          
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized - Please login again');
        } else if (response.status === 403) {
          throw new Error('Forbidden - You don\'t have permission');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const data = await response.json();
      console.log('Received fresh data:', data); // Debug log
      console.log('Total contributions from API:', data.summary.total_contributions); // Should be 8
      
      if (data.status && data.summary) {
        setStats(data.summary);
        setLastUpdated(new Date());
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to fetch statistics');
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      
      // Handle specific error types
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setError('Network error - Please check your connection');
      } else {
        setError(err.message || 'Error connecting to server');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial fetch on component mount or when token changes
  useEffect(() => {
    if (token) {
      fetchSummaryStats();
    } else {
      setError('Authentication required');
      setLoading(false);
    }
  }, [token, fetchSummaryStats, retryCount]); // Add retryCount to dependencies

  // Add a manual refresh function
  const handleRefresh = () => {
    setRetryCount(prev => prev + 1); // This will trigger a re-fetch
  };

  // Debug: Log stats whenever they change
  useEffect(() => {
    if (stats) {
      console.log('Current stats in state:', stats);
      console.log('Total contributions in state:', stats.total_contributions);
    }
  }, [stats]);

  // If no token, show login required message
  if (!token) {
    return (
      <div className="summary-stats error">
        <div className="error-message">
          <span className="error-icon">🔒</span>
          <p>Please log in to view dashboard statistics</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="summary-stats loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="summary-stats error">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <div>
            <p>{error}</p>
            {error.includes('Network error') && (
              <p className="error-hint">Please check your internet connection</p>
            )}
          </div>
        </div>
        <button onClick={handleRefresh} className="retry-btn">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="summary-stats">
      <div className="stats-header">
        <h2 className="stats-title">Dashboard Summary</h2>
        <div className="header-actions">
          {user && <span className="user-greeting">Welcome, {user.full_name || user.name || 'User'}</span>}
          <button onClick={handleRefresh} className="refresh-btn" title="Refresh data">
            🔄
          </button>
        </div>
      </div>
      
      <div className="stats-grid">
        {/* User Statistics */}
        <div className="stat-card users">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-label">Total Active Users</div>
            <div className="stat-value">{stats?.total_active_users || 0}</div>
          </div>
        </div>

        {/* Contributions Statistics */}
        <div className="stat-card contributions">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <div className="stat-label">Total Contributions</div>
            <div className="stat-value">{stats?.total_contributions || 0}</div>
          </div>
        </div>

        {/* Approved Count */}
        <div className="stat-card approved">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-label">Approved</div>
            <div className="stat-value">{stats?.approved_count || 0}</div>
          </div>
        </div>

        {/* Pending Count */}
        <div className="stat-card pending">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <div className="stat-label">Pending</div>
            <div className="stat-value">{stats?.pending_count || 0}</div>
          </div>
        </div>

        {/* Rejected Count */}
        <div className="stat-card rejected">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <div className="stat-label">Rejected</div>
            <div className="stat-value">{stats?.rejected_count || 0}</div>
          </div>
        </div>

        {/* Total Amount */}
        <div className="stat-card total-amount">
          <div className="stat-icon">💵</div>
          <div className="stat-content">
            <div className="stat-label">Total Amount</div>
            <div className="stat-value">
              ${parseFloat(stats?.total_amount || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </div>
          </div>
        </div>

        {/* Approved Amount */}
        <div className="stat-card approved-amount">
          <div className="stat-icon">💚</div>
          <div className="stat-content">
            <div className="stat-label">Approved Amount</div>
            <div className="stat-value">
              ${parseFloat(stats?.total_approved_amount || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </div>
          </div>
        </div>

        {/* Pending Amount */}
        <div className="stat-card pending-amount">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <div className="stat-label">Pending Amount</div>
            <div className="stat-value">
              ${parseFloat(stats?.total_pending_amount || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </div>
          </div>
        </div>

        {/* Rejected Amount */}
        <div className="stat-card rejected-amount">
          <div className="stat-icon">💔</div>
          <div className="stat-content">
            <div className="stat-label">Rejected Amount</div>
            <div className="stat-value">
              ${parseFloat(stats?.total_rejected_amount || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Debug Info - Remove in production */}
      {process.env.NODE_ENV === 'development' && stats && (
        <div className="debug-info" style={{marginTop: '10px', padding: '10px', background: '#f0f0f0', borderRadius: '5px', fontSize: '12px'}}>
          <strong>Debug:</strong> Total Contributions: {stats.total_contributions} | 
          Approved: {stats.approved_count} | 
          Rejected: {stats.rejected_count} | 
          Pending: {stats.pending_count}
        </div>
      )}

      {/* Last Updated Timestamp */}
      {stats && lastUpdated && (
        <div className="stats-footer">
          <small>Last updated: {lastUpdated.toLocaleString()}</small>
          <button onClick={handleRefresh} className="refresh-small-btn" title="Refresh data">
            ↻
          </button>
        </div>
      )}
    </div>
  );
};

export default SummaryStats;